import { api } from "./config";

// ── Financial dashboard (wallet liability/receivable + outstanding + branch cash)
export async function getFinancialDashboard() {
  try {
    const res = await api().get("/financial/dashboard");
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function getShippingPayments(status, page = 1, limit = 20, search = "", dateFrom = "", dateTo = "", customerId = "") {
  try {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append("status", status);
    if (customerId) params.append("customerId", customerId);
    else if (search) params.append("search", search);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    const res = await api().get(`/financial/shipping-payments?${params.toString()}`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function approveShippingPayment(id) {
  try {
    const res = await api().post(`/financial/shipping-payments/${id}/approve`);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function rejectShippingPayment(id, reason) {
  try {
    const res = await api().post(`/financial/shipping-payments/${id}/reject`, { reason });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function getBranchCash() {
  try {
    const res = await api().get("/financial/branch-cash");
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export async function depositBranchCash(formData) {
  try {
    const res = await api().post("/financial/cash-deposit", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Manager-only manual correction of a branch's cash drawer. `formData` carries
// stationId, currency, amount, direction ("add"|"deduct"), reason and an optional
// `evidence_0` image.
export async function adjustBranchCash(formData) {
  try {
    const res = await api().post("/financial/branch-cash/adjust", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// Collect shipping fees at pickup. `formData` carries customerId, stationId,
// packageIds (JSON), lines (JSON) and evidence_<i> image files.
// Returns { success, status: 'completed' | 'pending_approval', ... }.
export async function collectShippingPayment(formData) {
  try {
    const res = await api().post("/package/collect-payment", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

// ── Drill-down detail endpoints ──────────────────────────────────────────────
function qs(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function get(path) {
  try {
    const res = await api().get(path);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export const getWallets = (params) => get(`/financial/wallets${qs(params)}`);
export const getWalletTransactions = (accountId, params) =>
  get(`/financial/wallets/${accountId}/transactions${qs(params)}`);
export const getOutstandingStations = () => get(`/financial/outstanding-stations`);
export const getStationOutstanding = (stationId, params) =>
  get(`/financial/outstanding/${stationId}${qs(params)}`);
export const getCustomerParcels = (stationId, customerId, params) =>
  get(`/financial/outstanding/${stationId}/${customerId}${qs(params)}`);
export const getBranchCashTransactions = (stationId, params) =>
  get(`/financial/branch-cash/${stationId}/transactions${qs(params)}`);
export const getDailySummary = (stationId, params) =>
  get(`/financial/branch-cash/${stationId}/daily-summary${qs(params)}`);
export const getShippingPaymentDetail = (id) => get(`/financial/shipping-payments/${id}`);
export const getTransactionDetail = (id) => get(`/financial/transactions/${id}`);
export const getTopupDetail = (id) => get(`/financial/topups/${id}`);
export const getParcelDetail = (id) => get(`/financial/parcels/${id}`);

// ── Branch cash expenses (manager side: any station) ──────────────────────────
export const listBranchExpenses = (params) => get(`/financial/branch-expenses${qs(params)}`);
export const getBranchExpenseDetail = (id) => get(`/financial/branch-expenses/${id}`);

// ── Service charges (manager side: any station) ────────────────────────────────
export const listServiceCharges = (params) => get(`/financial/service-charges${qs(params)}`);
export const getServiceChargeDetail = (id) => get(`/financial/service-charges/${id}`);

async function post(path, body) {
  try {
    const res = await api().post(path, body);
    return res.data;
  } catch (error) {
    return error.response?.data || { success: false, message: error.message };
  }
}

export const approveBranchExpense = (id) => post(`/financial/branch-expenses/${id}/approve`);
export const rejectBranchExpense = (id, reason) => post(`/financial/branch-expenses/${id}/reject`, { reason });
export const approveServiceCharge = (id) => post(`/financial/service-charges/${id}/approve`);
export const rejectServiceCharge = (id, reason) => post(`/financial/service-charges/${id}/reject`, { reason });

// Evidence image URL (same-origin; httpOnly auth cookie is sent automatically).
export const evidenceUrl = (filename) =>
  filename ? `${api().defaults.baseURL || ""}/financial/evidence/${filename}` : "";
